import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Leaf, BadgeEuro, TrendingUp, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const iconMap = {
  Leaf: Leaf,
  BadgeEuro: BadgeEuro,
  TrendingUp: TrendingUp,
  Settings: Settings
};

const trustBadges = [
  { label: "ESG-konform", icon: "Leaf" },
  { label: "Förderfähig", icon: "BadgeEuro" },
  { label: "Skalierbar", icon: "TrendingUp" },
  { label: "Technologie-agnostisch", icon: "Settings" }
];

const heroImages = [
  {
    url: process.env.REACT_APP_HERO_IMAGE_1 || "https://customer-assets.emergentagent.com/job_bess-living/artifacts/gz8uaaha_QartierEnergy2.jpg",
    alt: "Nachhaltiges Wohnquartier mit Solaranlagen und Gründächern"
  },
  {
    url: process.env.REACT_APP_HERO_IMAGE_2 || "https://customer-assets.emergentagent.com/job_bess-living/artifacts/nxxtgqnu_QuartierEnergy3.jpg",
    alt: "Modernes Wohnquartier mit grünen Innenhöfen"
  },
  {
    url: process.env.REACT_APP_HERO_IMAGE_3 || "https://customer-assets.emergentagent.com/job_bess-living/artifacts/rnznm12g_QuartierEnergy4.jpg",
    alt: "Energieeffizientes Wohnquartier mit Holzfassaden"
  }
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning]);

  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  // Auto-advance every 8 seconds
  useEffect(() => {
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const scrollToForm = () => {
    document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative pt-16 sm:pt-20">
      {/* Mobile Layout: Image on top, content below */}
      <div className="lg:hidden">
        {/* Mobile Image Carousel */}
        <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          
          {/* Mobile Dots Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-white w-6' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Mobile Content - Optimized spacing and readability */}
        <div className="px-5 py-10 bg-gradient-to-b from-slate-50 to-white">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 mb-6">
            <span className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span className="text-xs text-emerald-700 font-medium">Energiezukunft gestalten</span>
          </div>

          {/* Headline - Optimized line breaks */}
          <h1 className="text-[1.625rem] leading-[1.3] font-semibold text-slate-800 mb-5">
            <span className="block">Das Wohnquartier</span>
            <span className="block">der Zukunft produziert,</span>
            <span className="block">speichert und vermarktet</span>
            <span className="block">Energie selbst.</span>
          </h1>

          {/* Subheadline - Better readability */}
          <p className="text-[0.9375rem] leading-[1.7] text-slate-600 mb-8">
            Wir entwickeln wirtschaftlich rentable Wohnquartiere mit integrierten Batteriespeichern – für niedrigere Energiekosten, ESG-Ziele und neue Erlöse.
          </p>

          {/* CTA Button */}
          <Button 
            onClick={scrollToForm}
            className="w-full bg-cyan-700 text-white hover:bg-cyan-800 font-medium text-[0.9375rem] px-6 py-4 rounded-lg transition-colors group mb-8"
          >
            Projekt anfragen
            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>

          {/* Trust Badges - Clean grid */}
          <div className="grid grid-cols-2 gap-3">
            {trustBadges.map((badge, index) => {
              const Icon = iconMap[badge.icon];
              return (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-slate-200"
                >
                  <Icon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-xs text-slate-700 font-medium">{badge.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop Layout: Full-width image with overlay */}
      <div className="hidden lg:block relative min-h-[85vh] xl:min-h-[90vh]">
        {/* Full-Width Image Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          
          {/* Gradient Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/50 to-transparent z-20" />
        </div>

        {/* Desktop Content Overlay */}
        <div className="relative z-30 max-w-7xl mx-auto px-6 lg:px-8 h-full min-h-[85vh] xl:min-h-[90vh] flex items-center">
          <div className="max-w-xl xl:max-w-2xl py-16">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
              <span className="w-2 h-2 bg-emerald-400 rounded-full" />
              <span className="text-sm text-white font-medium">Energiezukunft gestalten</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl xl:text-5xl font-semibold text-white leading-tight mb-6">
              Das Wohnquartier der Zukunft produziert, speichert und vermarktet Energie selbst.
            </h1>

            {/* Subheadline */}
            <p className="text-lg xl:text-xl text-slate-200 leading-relaxed mb-10">
              Wir entwickeln wirtschaftlich rentable Wohnquartiere mit integrierten Batteriespeichern – für niedrigere Energiekosten, ESG-Ziele und neue Erlöse.
            </p>

            {/* CTA Button */}
            <Button 
              onClick={scrollToForm}
              className="bg-cyan-600 text-white hover:bg-cyan-700 font-medium text-base px-8 py-6 rounded-lg transition-all hover:shadow-lg group mb-10"
            >
              Kostenlose Erstbewertung anfordern
              <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Button>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              {trustBadges.map((badge, index) => {
                const Icon = iconMap[badge.icon];
                return (
                  <div 
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
                  >
                    <Icon className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-white font-medium">{badge.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Vorheriges Bild"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Nächstes Bild"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Desktop Dots Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-30">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white w-8' 
                  : 'bg-white/40 w-2 hover:bg-white/60'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
